import React, {useMemo, useState} from 'react'
import dayjs from 'dayjs'
import {Box, Skeleton, Stack, ToggleButton, ToggleButtonGroup, Typography} from '@mui/material'
import {alpha, useTheme} from '@mui/material/styles'
import {Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, XAxis, YAxis} from 'recharts'
import {useTranslation} from 'react-i18next'
import {useGetMeterReadingTrendQuery} from 'store/Store'
import SectionCard from 'components/Page/Dashboard/SectionCard'
import DeltaBadge from 'components/Page/Dashboard/DeltaBadge'
import DashboardChartTooltip from 'components/Page/Dashboard/DashboardChartTooltip'
import {formatNumber, getUnit, percentChange} from 'components/Page/Dashboard/dashboardFormat'

const RANGES = ['day', 'week', 'month']

const formatLabel = (range, bucket, index) => {
    if (range === 'day') return `${String(index).padStart(2, '0')}:00`
    return range === 'week' ? dayjs(bucket).format('ddd D') : dayjs(bucket).format('D/M')
}

const ConsumptionChart = ({category}) => {
    const {t} = useTranslation()
    const {palette} = useTheme()
    const [range, setRange] = useState('day')
    const {data, isFetching} = useGetMeterReadingTrendQuery({range, category})
    const unit = getUnit(category)

    const {chartData, total, delta} = useMemo(() => {
        const points = data?.points || []
        // Hours after the last reading are left empty (instead of dropping to zero) so the line
        // ends where the data ends; silent meters are surfaced by the attention panel instead.
        const lastWithData = points.reduce((last, {total}, index) => (total > 0 ? index : last), -1)
        const rows = points.map((point, index) => ({
            label: formatLabel(data.range, point.bucket, index),
            usage: data.range === 'day' && index > lastWithData ? null : point.usage,
            previous: point.previousUsage,
        }))
        const elapsed = rows.filter(({usage}) => usage !== null)
        const currentTotal = elapsed.reduce((sum, {usage}) => sum + usage, 0)
        const previousTotal = rows
            .slice(0, elapsed.length)
            .reduce((sum, {previous}) => sum + previous, 0)
        return {
            chartData: rows,
            total: currentTotal,
            delta: percentChange(currentTotal, previousTotal),
        }
    }, [data])

    const hasData = chartData.some(({usage, previous}) => usage || previous)

    let content
    if (isFetching) {
        content = <Skeleton variant="rounded" height="100%" sx={{minHeight: 280}} />
    } else if (!hasData) {
        content = (
            <Box sx={{height: '100%', minHeight: 280, display: 'grid', placeItems: 'center'}}>
                <Typography variant="body2" color="text.secondary">
                    {t('dashboard.consumption.empty')}
                </Typography>
            </Box>
        )
    } else {
        content = (
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{top: 8, right: 8, left: 0, bottom: 0}}>
                    <defs>
                        <linearGradient id="consumptionFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={palette.primary.main} stopOpacity={0.28} />
                            <stop offset="100%" stopColor={palette.primary.main} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid
                        vertical={false}
                        strokeDasharray="3 3"
                        stroke={alpha(palette.divider, 0.6)}
                    />
                    <XAxis
                        dataKey="label"
                        tickLine={false}
                        axisLine={false}
                        minTickGap={24}
                        tick={{fontSize: 12, fill: palette.text.secondary}}
                    />
                    <YAxis
                        width={44}
                        tickLine={false}
                        axisLine={false}
                        tick={{fontSize: 12, fill: palette.text.secondary}}
                    />
                    <DashboardChartTooltip
                        formatter={(value, name) => [
                            `${formatNumber(value, 1)} ${unit}`,
                            name === 'usage'
                                ? t('dashboard.consumption.current')
                                : t('dashboard.consumption.previous'),
                        ]}
                    />
                    <Line
                        type="monotone"
                        dataKey="previous"
                        stroke={palette.text.disabled}
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        dot={false}
                        isAnimationActive={false}
                    />
                    <Area
                        type="monotone"
                        dataKey="usage"
                        stroke={palette.primary.main}
                        strokeWidth={2.5}
                        fill="url(#consumptionFill)"
                        dot={false}
                        activeDot={{r: 5}}
                        isAnimationActive={false}
                    />
                </ComposedChart>
            </ResponsiveContainer>
        )
    }

    return (
        <SectionCard
            title={t('dashboard.consumption.title')}
            subtitle={
                <Stack direction="row" columnGap={1} alignItems="baseline" flexWrap="wrap">
                    <Typography
                        variant="h6"
                        fontWeight={700}
                        color="text.primary"
                        sx={{whiteSpace: 'nowrap'}}>
                        <bdi>
                            {formatNumber(total, 1)} {unit}
                        </bdi>
                    </Typography>
                    <DeltaBadge value={delta} label={t(`dashboard.consumption.vs_${range}`)} />
                </Stack>
            }
            action={
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={range}
                    onChange={(_event, next) => next && setRange(next)}>
                    {RANGES.map((value) => (
                        <ToggleButton
                            key={value}
                            value={value}
                            sx={{px: 1.5, textTransform: 'none'}}>
                            {t(`dashboard.range.${value}`)}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            }>
            <Box sx={{height: 320}}>{content}</Box>
            <Stack direction="row" spacing={2.5} alignItems="center">
                <LegendDot
                    color={palette.primary.main}
                    label={t('dashboard.consumption.current')}
                />
                <LegendDot
                    color={palette.text.disabled}
                    label={t('dashboard.consumption.previous')}
                    dashed
                />
            </Stack>
        </SectionCard>
    )
}

const LegendDot = ({color, label, dashed}) => (
    <Stack direction="row" spacing={0.75} alignItems="center">
        <Box
            sx={{
                width: 16,
                height: 0,
                borderTop: `3px ${dashed ? 'dashed' : 'solid'} ${color}`,
            }}
        />
        <Typography variant="caption" color="text.secondary">
            {label}
        </Typography>
    </Stack>
)

export default ConsumptionChart
